import React, { useState, useEffect, useMemo, useCallback, useTransition, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  useTheme,
  alpha,
  Popover,
  Button,
  Divider,
  TextField,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Tabs,
  Tab,
  Slider,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Settings as SettingsIcon,
  Sync as SyncIcon,
  Info as InfoIcon,
  ShowChart as ShowChartIcon,
  ScatterPlot as ScatterPlotIcon,
} from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import {
  VariableSeleccionada,
  VariableHierarchyNode,
  EjeVariable,
  DatosHistoricosResponse,
  ActuadorData,
  Sensor,
  SensorData,
  TimestampAdjustment,
  EnsayoSamplingInfo,
  ExportVariableSpec,
} from '../../types';
import { apiService } from '../../services/api';
import { EjeVariables } from './EjeVariables';
import { AdvancedLineChart, ChartSeries, YAxisConfig } from '../charts/AdvancedLineChart';
import { CanvasScatterPlot } from '../charts/CanvasScatterPlot';
import type { ScatterDisplayPoint } from '../../types';
import { prepareChartSeries, prepareChartYAxes, generateOrangeFromPrimary } from '../../utils/chartUtils';
import { combineEnsayoDataToHistoricos } from '../../utils/ensayoDataConverter';
import { useToast } from '../../contexts/ToastContext';
import { useJobs } from '../../contexts/JobsContext';
import { getUserFacingApiMessage } from '../../utils/apiErrorMessage';
import {
  clampFineOffsetSeconds,
  parseFineOffsetInput,
  FINE_OFFSET_MIN,
  FINE_OFFSET_MAX,
} from '../../utils/fineOffset';
import {
  savePendingExportJob,
  readPendingExportJob,
  clearPendingExportJob,
} from '../../utils/exportJobSession';

function exportFileName(mode: 'all' | 'screen'): string {
  const d = new Date();
  const stamp = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}_${String(d.getHours()).padStart(2, '0')}-${String(d.getMinutes()).padStart(2, '0')}-${String(d.getSeconds()).padStart(2, '0')}`;
  return `datos_ensayo_${mode}_${stamp}.xlsx`;
}

function formatHz(hz: number | null | undefined): string {
  if (hz == null || !Number.isFinite(hz)) return '—';
  return `${hz.toFixed(1)} Hz`;
}

interface VisualizadorDatosEnsayoProps {
  ensayoId: string;
  actuadorData: ActuadorData[];
  actuadorDataTotal: number;
  sensores: Sensor[];
  datosPorSensor: Record<string, SensorData[]>;
  isLoading?: boolean;
  error?: string | null;
  onLoadSensorData?: (sensorId: string, movingAverageWindow?: number, useAdjustedTimestamps?: boolean) => Promise<void>;
  onLoadSensorsBatch?: (sensorIds: string[], movingAverageWindow?: number, useAdjustedTimestamps?: boolean) => Promise<void>;
  onMovingAverageChange?: (window: number | null, useAdjustedTimestamps?: boolean) => void;
}

export const VisualizadorDatosEnsayo: React.FC<VisualizadorDatosEnsayoProps> = ({
  ensayoId,
  actuadorData,
  actuadorDataTotal,
  sensores,
  datosPorSensor,
  isLoading = false,
  error = null,
  onLoadSensorData,
  onLoadSensorsBatch,
  onMovingAverageChange,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  // Configuración de drag and drop
  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: {
      distance: 8,
    },
  });
  const keyboardSensor = useSensor(KeyboardSensor, {
    coordinateGetter: sortableKeyboardCoordinates,
  });
  const sensors = useSensors(pointerSensor, keyboardSensor);

  // Construir jerarquía de variables desde los datos disponibles
  const hierarchy = useMemo<VariableHierarchyNode[]>(() => {
    const nodes: VariableHierarchyNode[] = [];

    // Agregar variables de actuador
    if (actuadorData.length > 0) {
      const actuadorNode: VariableHierarchyNode = {
        id: 'actuador-tipologia',
        nombre: 'Actuador',
        tipo: 'tipologia',
        children: [
          {
            id: 'actuador-sensor',
            nombre: 'Actuador',
            tipo: 'sensor',
            sensor_id: 'actuador',
            children: [
              {
                id: 'fuerza-variable',
                nombre: 'Fuerza',
                tipo: 'variable',
                variable_id: 'fuerza',
                sensor_id: 'actuador',
                variable_unidad: 'kN',
              },
              {
                id: 'desplazamiento-variable',
                nombre: 'Desplazamiento',
                tipo: 'variable',
                variable_id: 'desplazamiento',
                sensor_id: 'actuador',
                variable_unidad: 'mm',
              },
              {
                id: 'rigidez-variable',
                nombre: 'Rigidez',
                tipo: 'variable',
                variable_id: 'rigidez',
                sensor_id: 'actuador',
                variable_unidad: 'kN/mm',
              },
            ],
          },
        ],
      };
      nodes.push(actuadorNode);
    }

    // Agregar variables de sensores
    if (sensores.length > 0) {
      const sensoresPorTipo = new Map<string, Sensor[]>();
      sensores.forEach((sensor) => {
        const tipo = sensor.tipo || 'desconocido';
        if (!sensoresPorTipo.has(tipo)) {
          sensoresPorTipo.set(tipo, []);
        }
        sensoresPorTipo.get(tipo)!.push(sensor);
      });

      sensoresPorTipo.forEach((sensoresTipo, tipo) => {
        const tipoNode: VariableHierarchyNode = {
          id: `tipo-${tipo}`,
          nombre: tipo.charAt(0).toUpperCase() + tipo.slice(1).replace('_', ' '),
          tipo: 'tipologia',
          children: sensoresTipo.map((sensor) => {
            const children: VariableHierarchyNode[] = [];
            
            // Si el sensor tiene variables múltiples (FOS agrupado)
            if (sensor.variables && sensor.variables.length > 0) {
              // Crear un nodo por cada variable
              sensor.variables.forEach((variable) => {
                children.push({
                  id: `sensor_${sensor.id}_var_${variable.id}`,
                  nombre: variable.nombre_completo || variable.nombre_variable,
                  tipo: 'variable',
                  variable_id: `sensor_${sensor.id}_var_${variable.id}`,
                  sensor_id: sensor.id,
                  variable_unidad: variable.unidades || '',
                });
              });
            } else {
              // Sensor normal sin variables múltiples
              children.push({
                id: `sensor_${sensor.id}-variable`,
                nombre: sensor.nombre || `Sensor ${sensor.id.slice(0, 8)}`,
                tipo: 'variable',
                variable_id: `sensor_${sensor.id}`,
                sensor_id: sensor.id,
                variable_unidad: sensor.unidades || '',
              });
            }
            
            // Para sensores LVDT, siempre agregar opción de desplazamiento calculado
            // Los datos se cargarán automáticamente cuando se seleccione la variable
            if (tipo === 'lvdt') {
              children.push({
                id: `sensor_${sensor.id}-desplazamiento`,
                nombre: `${sensor.nombre} - Desplazamiento`,
                tipo: 'variable',
                variable_id: `sensor_${sensor.id}_desplazamiento`,
                sensor_id: sensor.id,
                variable_unidad: 'mm',
              });
            }
            
            // Para sensores FOS, siempre agregar opciones de strain y temperatura calculados
            // Los datos se cargarán automáticamente cuando se seleccione la variable
            if (tipo === 'fibra_optica') {
              children.push({
                id: `sensor_${sensor.id}-strain_calculado`,
                nombre: `${sensor.nombre} - Strain Calculado`,
                tipo: 'variable',
                variable_id: `sensor_${sensor.id}_strain_calculado`,
                sensor_id: sensor.id,
                variable_unidad: 'με',
              });
              
              children.push({
                id: `sensor_${sensor.id}-temperature_calculado`,
                nombre: `${sensor.nombre} - Temperatura Calculada`,
                tipo: 'variable',
                variable_id: `sensor_${sensor.id}_temperature_calculado`,
                sensor_id: sensor.id,
                variable_unidad: '°C',
              });
            }
            
            return {
              id: `${sensor.id}-sensor`,
              nombre: sensor.nombre || `Sensor ${sensor.id.slice(0, 8)}`,
              tipo: 'sensor',
              sensor_id: sensor.id,
              children,
            };
          }),
        };
        nodes.push(tipoNode);
      });
    }

    return nodes;
  }, [actuadorData, sensores, datosPorSensor]);

  const [selectedVariables, setSelectedVariables] = useState<VariableSeleccionada[]>([]);
  const [movingAverageWindow, setMovingAverageWindow] = useState<number | null>(null);
  const datosPorSensorRef = useRef(datosPorSensor);
  datosPorSensorRef.current = datosPorSensor;

  const reloadLoadedSensors = useCallback(
    (useAdjusted: boolean, extraSensorIds?: string[]) => {
      const ids = new Set<string>(Object.keys(datosPorSensorRef.current));
      extraSensorIds?.forEach((sid) => ids.add(sid));
      if (ids.size === 0) return;
      const sensorIdList = [...ids];
      const w = movingAverageWindow || undefined;
      if (onLoadSensorsBatch) {
        void onLoadSensorsBatch(sensorIdList, w, useAdjusted);
      } else if (onLoadSensorData) {
        sensorIdList.forEach((sensorId) => {
          void onLoadSensorData(sensorId, w, useAdjusted);
        });
      }
    },
    [onLoadSensorsBatch, onLoadSensorData, movingAverageWindow]
  );
  
  // Estados para ajuste de timestamps
  const [useOriginalTimestamps, setUseOriginalTimestamps] = useState(true);
  const [timestampAdjustments, setTimestampAdjustments] = useState<TimestampAdjustment[]>([]);
  const [isAdjustingTimestamps, setIsAdjustingTimestamps] = useState(false);
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);
  const [lastAdjustmentResult, setLastAdjustmentResult] = useState<TimestampAdjustment | null>(null);
  const [showCorrelationMetrics, setShowCorrelationMetrics] = useState(false);
  const [samplingInfo, setSamplingInfo] = useState<EnsayoSamplingInfo | null>(null);
  
  const { showError, showSuccess, showWarning } = useToast();
  const { addJob } = useJobs();
  
  // Handler para cambiar la media móvil y notificar al padre
  const handleMovingAverageChange = useCallback((window: number | null) => {
    setMovingAverageWindow(window);
    if (onMovingAverageChange) {
      onMovingAverageChange(window, !useOriginalTimestamps);
    }
  }, [onMovingAverageChange, useOriginalTimestamps]);
  
  // Cargar ajustes de timestamps
  const loadTimestampAdjustments = useCallback(async () => {
    try {
      const response = await apiService.getTimestampAdjustments(ensayoId, 'active');
      setTimestampAdjustments(response.adjustments);
    } catch (error) {
      console.error('Error loading timestamp adjustments:', error);
    }
  }, [ensayoId]);
  
  // Cargar ajustes al montar
  useEffect(() => {
    loadTimestampAdjustments();
  }, [loadTimestampAdjustments]);
  
  // Determinar si se puede reajustar timestamps
  const canReajustarTimestamps = useMemo(() => {
    if (selectedVariables.length !== 2) return false;
    
    const [var1, var2] = selectedVariables;
    // Deben estar en ejes diferentes
    if (var1.eje === var2.eje) return false;
    
    // Deben ser de fuentes diferentes
    if (var1.sensor_id === var2.sensor_id) return false;
    
    // Al menos una debe ser del actuador
    const hasActuador = var1.sensor_id === 'actuador' || var2.sensor_id === 'actuador';
    if (!hasActuador) return false;
    
    return true;
  }, [selectedVariables]);
  
  // Handler para reajustar timestamps
  const handleReajustarTimestamps = async () => {
    if (!canReajustarTimestamps || selectedVariables.length !== 2) return;
    
    const [var1, var2] = selectedVariables;
    
    // Determinar cuál es referencia (actuador) y cuál target (sensor)
    const reference = var1.sensor_id === 'actuador' ? var1 : var2;
    const target = var1.sensor_id === 'actuador' ? var2 : var1;
    
    if (reference.sensor_id !== 'actuador' || target.sensor_id === 'actuador') {
      showError('La referencia debe ser del actuador y el target de un sensor');
      return;
    }
    
    setIsAdjustingTimestamps(true);
    try {
      const adjustment = await apiService.createTimestampAdjustment(ensayoId, {
        source_type: 'datalogger',
        source_identifier: target.sensor_id, // Usar sensor_id como identificador del source
        reference_variable: reference.variable_id,
        target_variable_id: target.variable_id,
        target_sensor_id: target.sensor_id,
      });
      
      // Guardar el resultado del ajuste para mostrar las métricas
      setLastAdjustmentResult(adjustment);
      setShowCorrelationMetrics(true);
      
      // Determinar el mensaje según la calidad de la correlación
      const bestCorr = Math.max(
        Math.abs(Number(adjustment.correlation_pearson) || 0),
        Math.abs(Number(adjustment.correlation_spearman) || 0),
        Math.abs(Number(adjustment.correlation_trends) || 0)
      );

      const meta = adjustment.algorithm_metadata as Record<string, unknown> | undefined;
      const isStandaloneFirstRamp = meta?.method === 'standalone_first_ramp';

      if (isStandaloneFirstRamp) {
        showSuccess(
          'Ajuste aplicado por alineación de forma (lag entre señales). Las métricas Pearson pueden ser bajas si fuerza y sensor tienen escala distinta; comprueba el gráfico con «Ajustados».'
        );
      } else if (bestCorr >= 0.7) {
        showSuccess('Ajuste de timestamps calculado correctamente (correlación fuerte)');
      } else if (bestCorr >= 0.5) {
        showSuccess('Ajuste de timestamps calculado (correlación moderada)');
      } else if (bestCorr >= 0.3) {
        showSuccess('Ajuste de timestamps calculado (correlación débil - revisar métricas)');
      } else {
        showSuccess('Ajuste de timestamps calculado (correlación muy débil - revisar métricas)');
      }
      
      // Recargar ajustes
      await loadTimestampAdjustments();
      
      // El ajuste se aplica a todos los sensores: recargar según el modo actual (originales vs ajustados)
      setTimeout(() => {
        reloadLoadedSensors(!useOriginalTimestamps);
      }, 100);
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.message || 'Error al ajustar timestamps';
      
      // Si el error es sobre R² bajo, el backend debería haber devuelto el ajuste de todas formas
      // pero si no lo hizo, mostrar un mensaje informativo
      if (errorMessage.includes('R²') && (errorMessage.includes('menor') || errorMessage.includes('menor que'))) {
        // El backend debería haber devuelto el ajuste, pero si lanzó un error, 
        // mostrar un mensaje informativo sin ocultar la posibilidad de ver los resultados
        showError('Ajuste calculado con correlación baja. El backend puede haber rechazado el ajuste. Por favor, revisa los logs del backend.');
      } else {
        showError(errorMessage);
        setLastAdjustmentResult(null);
        setShowCorrelationMetrics(false);
      }
    } finally {
      setIsAdjustingTimestamps(false);
    }
  };
  
  // Verificar si hay ajustes
  const hasAdjustments = timestampAdjustments.length > 0;

  const activeAdjustment = useMemo(
    () => timestampAdjustments.find((a) => a.status === 'active') ?? null,
    [timestampAdjustments]
  );

  const adjustmentTraceSensorName = useMemo(() => {
    if (!activeAdjustment || activeAdjustment.source_type !== 'datalogger') return null;
    const s = sensores.find((x) => x.id === activeAdjustment.source_identifier);
    return s?.nombre ?? null;
  }, [activeAdjustment, sensores]);
  
  // Estados para ajuste de ejes Y
  const [axisSettingsOpen, setAxisSettingsOpen] = useState(false);
  const axisSettingsAnchorRef = React.useRef<HTMLButtonElement>(null);
  const [primaryAxisMin, setPrimaryAxisMin] = useState<number | undefined>(undefined);
  const [primaryAxisMax, setPrimaryAxisMax] = useState<number | undefined>(undefined);
  const [secondaryAxisMin, setSecondaryAxisMin] = useState<number | undefined>(undefined);
  const [secondaryAxisMax, setSecondaryAxisMax] = useState<number | undefined>(undefined);

  // Eje X variable (solo activo con timestamps enlazados)
  const hasTimestampsEnlazados = timestampAdjustments.length > 0 && !useOriginalTimestamps;
  const [xAxisVariable, setXAxisVariable] = useState<{ dataKey: string; label: string } | null>(null);

  // Tipo de gráfico: evolución temporal o scatter X-Y
  const [chartType, setChartType] = useState<'temporal' | 'scatter'>('temporal');

  const [showScatterRegression, setShowScatterRegression] = useState(false);
  const [scatterInputFrom, setScatterInputFrom] = useState<string>('');
  const [scatterInputTo, setScatterInputTo] = useState<string>('');
  const [scatterAppliedFrom, setScatterAppliedFrom] = useState<number | ''>('');
  const [scatterAppliedTo, setScatterAppliedTo] = useState<number | ''>('');
  const [fineOffsetSeconds, setFineOffsetSeconds] = useState<number>(0);
  const [fineOffsetDisplay, setFineOffsetDisplay] = useState<number>(0);
  const [, startTransition] = useTransition();

  // Sincronizar slider con fine_offset_seconds del ajuste activo (BD / refetch tras PATCH)
  useEffect(() => {
    const val =
      activeAdjustment != null
        ? Number(activeAdjustment.fine_offset_seconds) || 0
        : 0;
    setFineOffsetSeconds(val);
    setFineOffsetDisplay(val);
  }, [activeAdjustment?.id, activeAdjustment?.fine_offset_seconds]);

  // Persistir fine offset en backend cuando cambia (debounce implícito: solo en onChangeCommitted / Enter / blur)
  const persistFineOffset = useCallback(async (value: number) => {
    if (!Number.isFinite(value)) {
      showError(
        t('ensayos.visualizador.fineOffsetInvalid', 'Valor de offset no válido.')
      );
      return;
    }
    const clamped = clampFineOffsetSeconds(value);
    if (clamped !== value) {
      showWarning(
        t(
          'ensayos.visualizador.fineOffsetClamped',
          'El offset fino debe estar entre {{min}} y {{max}} s (se ha ajustado el valor).',
          { min: FINE_OFFSET_MIN, max: FINE_OFFSET_MAX }
        )
      );
      setFineOffsetDisplay(clamped);
      setFineOffsetSeconds(clamped);
    }
    const toSave = clamped;

    // Siempre el id del ajuste ACTIVO en este momento (evita 404 si antes hubo «Reajustar» y el closure tenía un id superseded)
    const adj = timestampAdjustments.find((a) => a.status === 'active');
    if (!adj) {
      showError(
        t(
          'ensayos.visualizador.fineOffsetNoActiveAdjustment',
          'No hay un ajuste de timestamps activo. Pulse «Reajustar timestamps» y espere a que termine antes de usar el offset fino.'
        )
      );
      return;
    }
    try {
      await apiService.updateFineOffset(ensayoId, adj.id, toSave);
    } catch (err: unknown) {
      console.error('Error saving fine offset:', err);
      const msg = getUserFacingApiMessage(err).trim();
      showError(
        msg ||
          t(
            'ensayos.visualizador.fineOffsetSaveError',
            'No se pudo guardar el offset fino. Inténtelo de nuevo.'
          )
      );
      return;
    }

    try {
      const w = movingAverageWindow ?? undefined;
      const reloadSensors =
        !useOriginalTimestamps && (onLoadSensorsBatch || onLoadSensorData)
          ? (() => {
              const ids = new Set<string>();
              Object.keys(datosPorSensor).forEach((sid) => ids.add(sid));
              selectedVariables.forEach((v) => {
                if (v.sensor_id && v.sensor_id !== 'actuador') ids.add(v.sensor_id);
              });
              if (ids.size === 0) return Promise.resolve();
              if (onLoadSensorsBatch) {
                return onLoadSensorsBatch([...ids], w, true);
              }
              return Promise.all(
                [...ids].map((sensorId) =>
                  onLoadSensorData!(sensorId, w, true).catch((e) => {
                    console.error(`Error recargando sensor ${sensorId} tras offset fino:`, e);
                  })
                )
              );
            })()
          : Promise.resolve();

      await Promise.all([
        loadTimestampAdjustments().catch((e) => {
          console.error('Recarga de ajustes tras offset fino (no bloquea guardado):', e);
        }),
        reloadSensors,
      ]);
    } catch (e) {
      console.error('Recarga tras offset fino:', e);
    }
  }, [
    ensayoId,
    timestampAdjustments,
    loadTimestampAdjustments,
    onLoadSensorData,
    onLoadSensorsBatch,
    useOriginalTimestamps,
    datosPorSensor,
    selectedVariables,
    movingAverageWindow,
    showError,
    showWarning,
    t,
  ]);

  useEffect(() => {
    if (!hasTimestampsEnlazados && xAxisVariable) setXAxisVariable(null);
  }, [hasTimestampsEnlazados]);

  // Paleta de colores corporativa
  const primaryColor = theme.palette.primary.main;
  const secondaryAxisColor = useMemo(
    () => generateOrangeFromPrimary(primaryColor),
    [primaryColor]
  );
  
  // Función para generar variaciones de un color base
  const generateColorVariations = useCallback((baseColor: string, count: number): string[] => {
    const hexToRgb = (hex: string): [number, number, number] => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result
        ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)]
        : [158, 184, 209];
    };

    const rgbToHex = (r: number, g: number, b: number): string => {
      return '#' + [r, g, b].map((x) => {
        const hex = Math.round(x).toString(16);
        return hex.length === 1 ? '0' + hex : hex;
      }).join('');
    };

    const [r, g, b] = hexToRgb(baseColor);
    const colors: string[] = [];

    for (let i = 0; i < count; i++) {
      const factor = i / Math.max(count - 1, 1);
      
      if (i === 0) {
        colors.push(baseColor);
      } else if (i % 3 === 1) {
        const lightR = Math.min(255, r + (255 - r) * 0.3);
        const lightG = Math.min(255, g + (255 - g) * 0.3);
        const lightB = Math.min(255, b + (255 - b) * 0.3);
        colors.push(rgbToHex(lightR, lightG, lightB));
      } else if (i % 3 === 2) {
        const darkR = Math.max(0, r * 0.7);
        const darkG = Math.max(0, g * 0.7);
        const darkB = Math.max(0, b * 0.7);
        colors.push(rgbToHex(darkR, darkG, darkB));
      } else {
        const satR = Math.min(255, Math.max(0, r + (factor - 0.5) * 30));
        const satG = Math.min(255, Math.max(0, g + (factor - 0.5) * 30));
        const satB = Math.min(255, Math.max(0, b + (factor - 0.5) * 30));
        colors.push(rgbToHex(satR, satG, satB));
      }
    }

    return colors;
  }, []);

  const generatePrimaryAxisColors = useCallback((count: number): string[] => {
    return generateColorVariations(primaryColor, count);
  }, [primaryColor, generateColorVariations]);

  const generateSecondaryAxisColors = useCallback((count: number): string[] => {
    return generateColorVariations(secondaryAxisColor, count);
  }, [secondaryAxisColor, generateColorVariations]);

  const recalculateVariableColors = useCallback(
    (variables: VariableSeleccionada[]): VariableSeleccionada[] => {
      const primaryVars = variables.filter((v) => v.eje === 'primary');
      const secondaryVars = variables.filter((v) => v.eje === 'secondary');

      const primaryColors = generatePrimaryAxisColors(primaryVars.length);
      const secondaryColors = generateSecondaryAxisColors(secondaryVars.length);

      let primaryIndex = 0;
      let secondaryIndex = 0;

      return variables.map((v) => {
        if (v.eje === 'primary') {
          const color = primaryColors[primaryIndex % primaryColors.length] || primaryColor;
          primaryIndex += 1;
          return { ...v, color };
        }
        const color =
          secondaryColors[secondaryIndex % secondaryColors.length] ||
          secondaryAxisColor;
        secondaryIndex += 1;
        return { ...v, color };
      });
    },
    [primaryColor, secondaryAxisColor, generatePrimaryAxisColors, generateSecondaryAxisColors]
  );

  const colorMap = useMemo(() => {
    const map: Record<string, string> = {};
    selectedVariables.forEach((v) => {
      const key = `${v.variable_id}-${v.sensor_id}`;
      if (v.color) {
        map[key] = v.color;
      }
    });
    return map;
  }, [selectedVariables]);

  // Convertir datos a formato DatosHistoricosResponse
  // NOTA: La media móvil y los ajustes de timestamps ahora se aplican en el backend
  // El backend aplica los ajustes cuando use_adjusted_timestamps=true
  const datosBase = useMemo<DatosHistoricosResponse | null>(() => {
    if (actuadorData.length === 0 && sensores.length === 0) {
      return null;
    }

    return combineEnsayoDataToHistoricos(
      actuadorData,
      sensores,
      datosPorSensor
    );
  }, [actuadorData, sensores, datosPorSensor]);

  // datos apunta a datosBase; el offset fino del sensor va en la API cuando timestamps ajustados.
  const datos = datosBase;

  useEffect(() => {
    if (!ensayoId) return;
    let cancelled = false;
    apiService
      .getEnsayoSamplingInfo(ensayoId)
      .then((info) => {
        if (!cancelled) setSamplingInfo(info);
      })
      .catch((err) => {
        console.error('Error loading sampling info:', err);
      });
    return () => {
      cancelled = true;
    };
  }, [ensayoId, actuadorDataTotal, sensores.length]);

  const xAxisVariableOptions = useMemo(() => {
    if (!datos?.variables) return [];
    return datos.variables
      .filter((v) => v.datos.length > 0)
      .map((v) => {
        const dataKey = `${v.variable_id}_${v.sensor_id}`.replace(/[^a-zA-Z0-9_]/g, '_');
        const label = `${v.sensor_nombre || 'Sensor'} - ${v.variable_nombre}${v.variable_unidad ? ` (${v.variable_unidad})` : ''}`;
        return { dataKey, label };
      });
  }, [datos]);

  // Recargar datos cuando cambia el switch de timestamps (no en montaje inicial)
  const isFirstTimestampEffect = useRef(true);
  useEffect(() => {
    if (isFirstTimestampEffect.current) {
      isFirstTimestampEffect.current = false;
      return;
    }
    const useAdjusted = !useOriginalTimestamps;
    const timeoutId = setTimeout(() => {
      reloadLoadedSensors(useAdjusted);
    }, 100);
    return () => clearTimeout(timeoutId);
  }, [useOriginalTimestamps, reloadLoadedSensors]);

  // Preparar series para AdvancedLineChart (excluir variable del eje X si está seleccionada)
  const chartSeries = useMemo((): ChartSeries[] => {
    const getSensorNombre = (variable: VariableSeleccionada): string | null => {
      for (const tipologiaNode of hierarchy) {
        if (tipologiaNode.children) {
          const sensorNode = tipologiaNode.children.find(
            (node) => node.sensor_id === variable.sensor_id && node.tipo === 'sensor'
          );
          if (sensorNode) {
            return sensorNode.nombre;
          }
        }
      }
      return null;
    };

    const varsForSeries = xAxisVariable
      ? selectedVariables.filter((v) => {
          const dk = `${v.variable_id}_${v.sensor_id}`.replace(/[^a-zA-Z0-9_]/g, '_');
          return dk !== xAxisVariable.dataKey;
        })
      : selectedVariables;

    return prepareChartSeries(
      datos,
      varsForSeries,
      primaryColor,
      secondaryAxisColor,
      false,
      colorMap,
      getSensorNombre
    );
  }, [datos, selectedVariables, hierarchy, colorMap, primaryColor, secondaryAxisColor, xAxisVariable]);

  // Calcular valores automáticos de los ejes basados en los datos
  const autoAxisValues = useMemo(() => {
    if (!datos || !datos.variables || chartSeries.length === 0) {
      return { primary: { min: undefined, max: undefined }, secondary: { min: undefined, max: undefined } };
    }

    const primaryValues: number[] = [];
    const secondaryValues: number[] = [];

    datos.variables.forEach((variable) => {
      const serie = chartSeries.find(
        (s) => s.key === `${variable.variable_id}-${variable.sensor_id}`
      );
      if (serie) {
        variable.datos.forEach((dato) => {
          if (dato.valor !== null && dato.valor !== undefined && typeof dato.valor === 'number') {
            if (serie.yAxisId === 'primary') {
              primaryValues.push(dato.valor);
            } else if (serie.yAxisId === 'secondary') {
              secondaryValues.push(dato.valor);
            }
          }
        });
      }
    });

    const calculateDomain = (values: number[]) => {
      if (values.length === 0) return { min: undefined, max: undefined };
      const min = Math.min(...values);
      const max = Math.max(...values);
      const range = max - min;
      const padding = range * 0.1 || Math.abs(max) * 0.1 || 1;
      return {
        min: min - padding,
        max: max + padding,
      };
    };

    return {
      primary: calculateDomain(primaryValues),
      secondary: calculateDomain(secondaryValues),
    };
  }, [datos, chartSeries]);

  // Preparar configuración de ejes Y con valores min/max personalizados
  const chartYAxes = useMemo((): YAxisConfig[] => {
    const axes = prepareChartYAxes(selectedVariables);
    
    return axes.map((axis) => {
      if (axis.id === 'primary') {
        return {
          ...axis,
          min: primaryAxisMin,
          max: primaryAxisMax,
        };
      } else if (axis.id === 'secondary') {
        return {
          ...axis,
          min: secondaryAxisMin,
          max: secondaryAxisMax,
        };
      }
      return axis;
    });
  }, [selectedVariables, primaryAxisMin, primaryAxisMax, secondaryAxisMin, secondaryAxisMax]);

  // Estado para el menú de exportación
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportDialogKind, setExportDialogKind] = useState<'all' | 'screen' | null>(null);
  const [scatterData, setScatterData] = useState<ScatterDisplayPoint[]>([]);
  const [isLoadingScatter, setIsLoadingScatter] = useState(false);
  
  const handleExportClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setExportMenuAnchor(event.currentTarget);
  };

  const handleExportClose = () => {
    setExportMenuAnchor(null);
  };

  const closeExportDialog = useCallback(() => {
    setExportDialogOpen(false);
    setExportDialogKind(null);
    setIsExporting(false);
  }, []);

  const registerExportAllJob = useCallback(
    (jobId: string, filename: string, options?: { openDialog?: boolean }) => {
      savePendingExportJob(ensayoId, { jobId, filename, startedAt: Date.now() });

      if (options?.openDialog) {
        setExportDialogKind('all');
        setExportDialogOpen(true);
        setIsExporting(false);
      }

      const jobKey = `export-${ensayoId}-${jobId}`;
      addJob({
        id: jobKey,
        title: t(
          'ensayos.visualizador.exportJobTitle',
          'Exportación Excel (todos los sensores)'
        ),
        pollingFn: async () => {
          const statusResponse = await apiService.getEnsayoExportJobStatus(ensayoId, jobId);
          if (statusResponse.status === 'COMPLETED') return 'COMPLETED';
          if (statusResponse.status === 'ERROR') return 'ERROR';
          return 'PROCESSING';
        },
        onComplete: async () => {
          clearPendingExportJob(ensayoId);
          closeExportDialog();
          try {
            const result = await apiService.downloadEnsayoExportJob(ensayoId, jobId, filename);
            showSuccess(
              t('ensayos.visualizador.exportSuccessDetail', {
                defaultValue: 'Datos exportados: {{rows}} filas a {{hz}}',
                rows: result.rowCount ?? '—',
                hz: formatHz(result.samplingRateHz),
              })
            );
          } catch (downloadErr: unknown) {
            showError(getUserFacingApiMessage(downloadErr) || 'Error al descargar la exportación');
          }
        },
        onError: async () => {
          clearPendingExportJob(ensayoId);
          closeExportDialog();
          try {
            const statusResponse = await apiService.getEnsayoExportJobStatus(ensayoId, jobId);
            showError(
              statusResponse.error ||
                t(
                  'ensayos.visualizador.exportJobFailed',
                  'Error al generar la exportación completa.'
                )
            );
          } catch {
            showError(
              t(
                'ensayos.visualizador.exportJobFailed',
                'Error al generar la exportación completa.'
              )
            );
          }
        },
      });
    },
    [ensayoId, addJob, closeExportDialog, showSuccess, showError, t]
  );

  const exportRecoveryStarted = useRef(false);
  useEffect(() => {
    if (exportRecoveryStarted.current) return;
    exportRecoveryStarted.current = true;

    const pending = readPendingExportJob(ensayoId);
    if (!pending) return;

    void (async () => {
      try {
        const status = await apiService.getEnsayoExportJobStatus(ensayoId, pending.jobId);
        if (status.status === 'COMPLETED') {
          clearPendingExportJob(ensayoId);
          const result = await apiService.downloadEnsayoExportJob(
            ensayoId,
            pending.jobId,
            pending.filename
          );
          showSuccess(
            t('ensayos.visualizador.exportSuccessDetail', {
              defaultValue: 'Datos exportados: {{rows}} filas a {{hz}}',
              rows: result.rowCount ?? '—',
              hz: formatHz(result.samplingRateHz),
            })
          );
          return;
        }
        if (status.status === 'ERROR') {
          clearPendingExportJob(ensayoId);
          showError(
            status.error ||
              t(
                'ensayos.visualizador.exportJobFailed',
                'Error al generar la exportación completa.'
              )
          );
          return;
        }
        registerExportAllJob(pending.jobId, pending.filename, { openDialog: true });
      } catch {
        clearPendingExportJob(ensayoId);
      }
    })();
  }, [ensayoId, registerExportAllJob, showSuccess, showError, t]);

  const ensureFineOffsetSavedBeforeExport = useCallback(async (): Promise<boolean> => {
    if (!activeAdjustment || useOriginalTimestamps) return true;
    const saved = Number(activeAdjustment.fine_offset_seconds) || 0;
    if (Math.abs(saved - fineOffsetSeconds) < 0.001) return true;
    showWarning(
      t(
        'ensayos.visualizador.exportSavingFineOffset',
        'Guardando el offset fino antes de exportar…'
      )
    );
    try {
      await apiService.updateFineOffset(ensayoId, activeAdjustment.id, fineOffsetSeconds);
      return true;
    } catch (err: unknown) {
      showError(
        getUserFacingApiMessage(err) ||
          t(
            'ensayos.visualizador.exportFineOffsetRequired',
            'Guarde el offset fino antes de exportar (ajuste fino del sensor).'
          )
      );
      return false;
    }
  }, [
    activeAdjustment,
    useOriginalTimestamps,
    fineOffsetSeconds,
    ensayoId,
    showWarning,
    showError,
    t,
  ]);

  const buildTemporalExportVariables = useCallback((): ExportVariableSpec[] => {
    const specs: ExportVariableSpec[] = [];
    const seen = new Set<string>();

    const add = (variableId: string, sensorId: string) => {
      const key = `${variableId}::${sensorId}`;
      if (!seen.has(key)) {
        seen.add(key);
        specs.push({ variable_id: variableId, sensor_id: sensorId });
      }
    };

    if (xAxisVariable && datos?.variables) {
      const match = datos.variables.find((v) => {
        const dk = `${v.variable_id}_${v.sensor_id}`.replace(/[^a-zA-Z0-9_]/g, '_');
        return dk === xAxisVariable.dataKey;
      });
      if (match) add(match.variable_id, match.sensor_id);
    }

    selectedVariables.forEach((v) => add(v.variable_id, v.sensor_id));
    return specs;
  }, [xAxisVariable, datos, selectedVariables]);

  const generateExcel = async (mode: 'all' | 'screen') => {
    handleExportClose();

    if (actuadorData.length === 0 && sensores.length === 0) {
      showError('No hay datos ni sensores disponibles para exportar');
      return;
    }

    setExportDialogKind(mode);
    setExportDialogOpen(true);
    setIsExporting(true);

    try {
      const offsetOk = await ensureFineOffsetSavedBeforeExport();
      if (!offsetOk) {
        showError(
          t(
            'ensayos.visualizador.exportFineOffsetRequired',
            'Guarde el offset fino antes de exportar (ajuste fino del sensor).'
          )
        );
        closeExportDialog();
        return;
      }

      const useAdjusted = !useOriginalTimestamps;
      const maWindow = movingAverageWindow || undefined;

      if (mode === 'all') {
        const filename = exportFileName('all');
        const { job_id: jobId } = await apiService.startEnsayoExportJob(ensayoId, {
          mode: 'all',
          useAdjustedTimestamps: useAdjusted,
          movingAverageWindow: maWindow,
        });

        registerExportAllJob(jobId, filename);
        setIsExporting(false);
        return;
      }

      if (chartType === 'scatter') {
        const xv = primaryVariables[0];
        const yv = secondaryVariables[0];
        if (!xv || !yv) {
          showError('Seleccione variables en eje primario y secundario para exportar el scatter.');
          closeExportDialog();
          return;
        }
        const from =
          scatterAppliedFrom === '' ? undefined : Number(scatterAppliedFrom);
        const to = scatterAppliedTo === '' ? undefined : Number(scatterAppliedTo);
        const result = await apiService.downloadEnsayoExport(
          ensayoId,
          {
            mode: 'scatter',
            useAdjustedTimestamps: useAdjusted,
            movingAverageWindow: maWindow,
            variables: [
              { variable_id: xv.variable_id, sensor_id: xv.sensor_id },
              { variable_id: yv.variable_id, sensor_id: yv.sensor_id },
            ],
            timeFrom: from !== undefined && !isNaN(from) ? from : undefined,
            timeTo: to !== undefined && !isNaN(to) ? to : undefined,
          },
          exportFileName('screen')
        );
        showSuccess(
          t('ensayos.visualizador.exportSuccessDetail', {
            defaultValue: 'Datos exportados: {{rows}} filas a {{hz}}',
            rows: result.rowCount ?? '—',
            hz: formatHz(result.samplingRateHz),
          })
        );
        closeExportDialog();
        return;
      }

      const temporalVars = buildTemporalExportVariables();
      if (temporalVars.length === 0) {
        showError('No hay series en el gráfico para exportar.');
        closeExportDialog();
        return;
      }

      const result = await apiService.downloadEnsayoExport(
        ensayoId,
        {
          mode: 'temporal',
          useAdjustedTimestamps: useAdjusted,
          movingAverageWindow: maWindow,
          variables: temporalVars,
        },
        exportFileName('screen')
      );
      showSuccess(
        t('ensayos.visualizador.exportSuccessDetail', {
          defaultValue: 'Datos exportados: {{rows}} filas a {{hz}}',
          rows: result.rowCount ?? '—',
          hz: formatHz(result.samplingRateHz),
        })
      );
      closeExportDialog();
    } catch (err: unknown) {
      console.error('Error exporting:', err);
      showError(getUserFacingApiMessage(err) || 'Error al exportar a Excel');
      closeExportDialog();
    }
  };

  // Handlers para drag and drop
  const handleVariableAdd = async (variableId: string, sensorId: string, sensorNombre: string | null, eje: EjeVariable = 'primary') => {
    // Si es un sensor (voltaje o desplazamiento) y no tiene datos cargados, cargarlos
    // Las variables de desplazamiento también empiezan con "sensor_" (sensor_${sensor.id}_desplazamiento)
    // Los desplazamientos usan los mismos datos del sensor, así que se cargan automáticamente con los timestamps ajustados
    if (variableId.startsWith('sensor_') && onLoadSensorData) {
      // Verificar si los datos están cargados o si el array está vacío
      const hasData = datosPorSensor[sensorId] && datosPorSensor[sensorId].length > 0;
      if (!hasData) {
        // Cargar datos con timestamps ajustados según el switch
        // Esto asegura que tanto el voltaje como los desplazamientos usen los mismos timestamps ajustados
        await onLoadSensorData(sensorId, movingAverageWindow || undefined, !useOriginalTimestamps);
      }
      // Si los datos ya están cargados, no es necesario recargarlos aquí
      // El useEffect se encarga de recargar cuando cambia el switch de timestamps
    }

    let variableInfo: VariableHierarchyNode | null = null;
    let finalSensorNombre: string | null = sensorNombre;

    for (const tipologiaNode of hierarchy) {
      if (tipologiaNode.children) {
        for (const sensorNode of tipologiaNode.children) {
          if (sensorNode.sensor_id === sensorId && sensorNode.tipo === 'sensor') {
            if (!finalSensorNombre) {
              finalSensorNombre = sensorNode.nombre;
            }
            if (sensorNode.children) {
              variableInfo = sensorNode.children.find(
                (v) => v.variable_id === variableId && v.tipo === 'variable'
              ) || null;
            }
            break;
          }
        }
      }
    }

    // También buscar variables del actuador directamente en la jerarquía
    if (!variableInfo) {
      for (const tipologiaNode of hierarchy) {
        if (tipologiaNode.children) {
          for (const sensorNode of tipologiaNode.children) {
            if (sensorNode.sensor_id === sensorId && sensorNode.children) {
              variableInfo = sensorNode.children.find(
                (v) => v.variable_id === variableId && v.tipo === 'variable'
              ) || null;
              if (variableInfo) break;
            }
          }
          if (variableInfo) break;
        }
      }
    }

    if (variableInfo) {
      const newVariable: VariableSeleccionada = {
        variable_id: variableId,
        variable_nombre: variableInfo.nombre,
        variable_unidad: variableInfo.variable_unidad || '',
        sensor_id: sensorId,
        sensor_nombre: finalSensorNombre,
        eje: eje, // Usar el eje proporcionado en lugar de siempre 'primary'
        agregacion: 'AVG',
      };
      setSelectedVariables((prev) => {
        const exists = prev.some(
          (v) => v.variable_id === variableId && v.sensor_id === sensorId
        );
        if (exists) return prev;
        const updated = [...prev, newVariable];
        return recalculateVariableColors(updated);
      });
    }
  };

  const handleVariableMove = (variableId: string, sensorId: string, newEje: EjeVariable) => {
    setSelectedVariables((prev) => {
      const updated = prev.map((v) =>
        v.variable_id === variableId && v.sensor_id === sensorId
          ? { ...v, eje: newEje }
          : v
      );
      return recalculateVariableColors(updated);
    });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over) return;

    const activeId = active.id as string;
    const [variableId, sensorId] = activeId.split('-');

    if (over.id === 'eje-primary' || over.id === 'eje-secondary') {
      const targetEje = over.id === 'eje-primary' ? 'primary' : 'secondary';
      const currentVar = selectedVariables.find(
        (v) => v.variable_id === variableId && v.sensor_id === sensorId
      );

      if (currentVar && currentVar.eje !== targetEje) {
        handleVariableMove(variableId, sensorId, targetEje);
      } else if (!currentVar) {
        let sensorNombre: string | null = null;
        for (const tipologiaNode of hierarchy) {
          if (tipologiaNode.children) {
            const sensorNode = tipologiaNode.children.find(
              (node) => node.sensor_id === sensorId && node.tipo === 'sensor'
            );
            if (sensorNode) {
              sensorNombre = sensorNode.nombre;
              break;
            }
          }
        }
        // Pasar el eje directamente al agregar la variable
        handleVariableAdd(variableId, sensorId, sensorNombre, targetEje);
      }
    }
  };

  const handleVariableRemove = (variableId: string, sensorId: string) => {
    setSelectedVariables((prev) => {
      const filtered = prev.filter(
        (v) => !(v.variable_id === variableId && v.sensor_id === sensorId)
      );
      return recalculateVariableColors(filtered);
    });
  };

  // Handlers para ajuste de ejes
  const handleResetPrimaryAxis = () => {
    setPrimaryAxisMin(undefined);
    setPrimaryAxisMax(undefined);
  };

  const handleResetSecondaryAxis = () => {
    setSecondaryAxisMin(undefined);
    setSecondaryAxisMax(undefined);
  };

  const handleResetAllAxes = () => {
    handleResetPrimaryAxis();
    handleResetSecondaryAxis();
  };

  const hasCustomAxisValues = 
    primaryAxisMin !== undefined || 
    primaryAxisMax !== undefined || 
    secondaryAxisMin !== undefined || 
    secondaryAxisMax !== undefined;

  const primaryVariables = useMemo(
    () => selectedVariables.filter((v) => v.eje === 'primary'),
    [selectedVariables]
  );

  const secondaryVariables = useMemo(
    () => selectedVariables.filter((v) => v.eje === 'secondary'),
    [selectedVariables]
  );

  // Variables para scatter: primera del eje primario (X) y primera del eje secundario (Y)
  const scatterXVariable = useMemo(() => {
    const v = primaryVariables[0];
    if (!v) return null;
    const dataKey = `${v.variable_id}_${v.sensor_id}`.replace(/[^a-zA-Z0-9_]/g, '_');
    const label = `${v.sensor_nombre || 'Sensor'} - ${v.variable_nombre}${v.variable_unidad ? ` (${v.variable_unidad})` : ''}`;
    return { dataKey, label };
  }, [primaryVariables]);

  const scatterYVariable = useMemo(() => {
    const v = secondaryVariables[0];
    if (!v) return null;
    const dataKey = `${v.variable_id}_${v.sensor_id}`.replace(/[^a-zA-Z0-9_]/g, '_');
    const label = `${v.sensor_nombre || 'Sensor'} - ${v.variable_nombre}${v.variable_unidad ? ` (${v.variable_unidad})` : ''}`;
    return { dataKey, label, variableId: v.variable_id, sensorId: v.sensor_id };
  }, [secondaryVariables]);

  useEffect(() => {
    const xVar = primaryVariables[0];
    const yVar = secondaryVariables[0];
    if (chartType !== 'scatter' || !xVar || !yVar) {
      setScatterData([]);
      setIsLoadingScatter(false);
      return;
    }

    let cancelled = false;
    setIsLoadingScatter(true);
    apiService
      .getScatterDisplayData(ensayoId, {
        xVariableId: xVar.variable_id,
        xSensorId: xVar.sensor_id,
        yVariableId: yVar.variable_id,
        ySensorId: yVar.sensor_id,
        maxDisplayPoints: samplingInfo?.display_max_points ?? 2000,
        useAdjustedTimestamps: !useOriginalTimestamps,
        movingAverageWindow: movingAverageWindow || undefined,
      })
      .then((resp) => {
        if (!cancelled) setScatterData(resp.points);
      })
      .catch((err) => {
        console.error('Error loading scatter display data:', err);
        if (!cancelled) setScatterData([]);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingScatter(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    chartType,
    ensayoId,
    primaryVariables,
    secondaryVariables,
    useOriginalTimestamps,
    movingAverageWindow,
    samplingInfo?.display_max_points,
  ]);

  const scatterTimestampRange = useMemo(() => {
    if (scatterData.length === 0) return { min: 0, max: 0 };
    const ts = scatterData.map((p) => p.timestamp);
    return { min: Math.min(...ts), max: Math.max(...ts) };
  }, [scatterData]);

  const filteredScatterData = useMemo(() => {
    if (scatterData.length === 0) return [];
    const from = scatterAppliedFrom === '' ? scatterTimestampRange.min : Number(scatterAppliedFrom);
    const to = scatterAppliedTo === '' ? scatterTimestampRange.max : Number(scatterAppliedTo);
    if (isNaN(from) || isNaN(to)) return scatterData;
    return scatterData.filter((p) => p.timestamp >= from && p.timestamp <= to);
  }, [scatterData, scatterAppliedFrom, scatterAppliedTo, scatterTimestampRange]);

  return (
    <Box
      sx={{
        p: 2,
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
      }}
    >
      {/* Header compacto */}
      <Box
        sx={{
          mb: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
            background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryAxisColor} 100%)`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        >
          Visualizador de Datos
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Ajustar ejes Y">
            <span>
              <IconButton
                ref={axisSettingsAnchorRef}
                onClick={() => setAxisSettingsOpen(true)}
                sx={{
                  backgroundColor: hasCustomAxisValues
                    ? alpha(primaryColor, 0.2)
                    : alpha(primaryColor, 0.1),
                  '&:hover': {
                    backgroundColor: alpha(primaryColor, 0.2),
                  },
                  border: hasCustomAxisValues
                    ? `2px solid ${primaryColor}`
                    : 'none',
                }}
              >
                <SettingsIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Exportar a Excel">
            <span>
              <IconButton
                onClick={handleExportClick}
                disabled={isExporting || (isLoading && !datos) || (actuadorData.length === 0 && sensores.length === 0)}
                sx={{
                  backgroundColor: alpha(primaryColor, 0.1),
                  '&:hover': {
                    backgroundColor: alpha(primaryColor, 0.2),
                  },
                }}
              >
                <DownloadIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => {}}>
          {error}
        </Alert>
      )}

      {samplingInfo && (
        <Alert severity="info" sx={{ mb: 2 }} icon={<InfoIcon fontSize="inherit" />}>
          <Typography variant="body2">
            {t('ensayos.visualizador.samplingInfo', {
              defaultValue:
                'Muestreo ensayo: {{native}} · Visualización: ~{{display}} (máx. {{maxPts}} pts) · Exportación: {{exportHz}} (frecuencia de referencia)',
              native: formatHz(samplingInfo.actuador_native_hz),
              display: formatHz(samplingInfo.display_hz),
              maxPts: samplingInfo.display_max_points,
              exportHz: formatHz(samplingInfo.reference_hz),
            })}
          </Typography>
        </Alert>
      )}

      {/* Flag de advertencia si hay ajustes pero se usan originales */}
      {hasAdjustments && useOriginalTimestamps && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          No se están aplicando los timestamps ajustados. Hay un ajuste activo que se aplica a todos los sensores del ensayo.
        </Alert>
      )}

      {activeAdjustment && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2" component="span">
            {t('ensayos.visualizador.adjustmentTrace', {
              ref:
                activeAdjustment.reference_variable === 'fuerza'
                  ? t('ensayos.visualizador.refFuerza', 'Fuerza')
                  : activeAdjustment.reference_variable === 'desplazamiento'
                    ? t('ensayos.visualizador.refDesplazamiento', 'Desplazamiento')
                    : activeAdjustment.reference_variable === 'rigidez'
                      ? t('ensayos.visualizador.refRigidez', 'Rigidez')
                      : activeAdjustment.reference_variable,
              sensor:
                adjustmentTraceSensorName ??
                (activeAdjustment.source_identifier
                  ? `${activeAdjustment.source_identifier.slice(0, 8)}…`
                  : '—'),
            })}
          </Typography>
        </Alert>
      )}

      {/* Controles: Timestamps y Media móvil */}
      <Box
        sx={{
          mb: 2,
          display: 'flex',
          gap: 2,
          flexWrap: 'wrap',
          alignItems: 'flex-start',
        }}
      >
        {/* Controles de Timestamps */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: theme.palette.text.primary,
              }}
            >
              Timestamps:
            </Typography>
            <Switch
              checked={!useOriginalTimestamps}
              onChange={(e) => setUseOriginalTimestamps(!e.target.checked)}
              size="small"
            />
            <Typography variant="body2">
              {useOriginalTimestamps ? 'Originales' : 'Ajustados'}
            </Typography>
            
            {/* Tooltip de métricas de ajuste activo */}
            {(() => {
              const activeAdjustment = timestampAdjustments.find(a => a.status === 'active');
              
              if (!activeAdjustment) {
                 // Si no hay ajuste activo, mostramos el tooltip informativo del algoritmo (comportamiento original)
                 return (
                  <Tooltip 
                    arrow
                    placement="right"
                    slotProps={{
                      tooltip: {
                        sx: {
                          bgcolor: theme.palette.primary.dark,
                          color: '#FFFFFF',
                          borderRadius: 2,
                          boxShadow: theme.shadows[8],
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                          '& .MuiTooltip-arrow': {
                            color: theme.palette.primary.dark,
                          },
                        },
                      },
                    }}
                    title="Información sobre el algoritmo de ajuste de timestamps"
                  >
                    <IconButton
                      size="small"
                      onClick={() => setInfoDialogOpen(true)}
                      sx={{ 
                        ml: 0.5,
                        color: theme.palette.primary.main,
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        '&:hover': {
                          bgcolor: alpha(theme.palette.primary.main, 0.2),
                        }
                      }}
                    >
                      <InfoIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                 );
              }

              // Si hay ajuste activo, mostramos las métricas
              return (
                <Tooltip
                  arrow
                  placement="right"
                  slotProps={{
                    tooltip: {
                      sx: {
                        bgcolor: theme.palette.primary.dark,
                        color: '#FFFFFF',
                        borderRadius: 2,
                        boxShadow: theme.shadows[8],
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        p: 0,
                        '& .MuiTooltip-arrow': {
                          color: theme.palette.primary.dark,
                        },
                      },
                    },
                  }}
                  title={
                    <Box sx={{ p: 1.5, minWidth: 220 }}>
                      <Typography 
                        variant="subtitle2" 
                        sx={{ 
                          fontWeight: 700, 
                          mb: 1.5, 
                          color: theme.palette.secondary.main,
                          borderBottom: '1px solid rgba(255, 255, 255, 0.2)', 
                          pb: 0.5,
                          fontSize: '0.75rem',
                          letterSpacing: '0.02em',
                        }}
                      >
                        Métricas del Ajuste Activo
                      </Typography>
                      <Box sx={{ display: 'grid', gridTemplateColumns: 'auto auto', gap: '8px 16px', alignItems: 'center' }}>
                        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.75)', fontWeight: 500 }}>R²:</Typography>
                        <Typography variant="caption" sx={{ fontWeight: 700, textAlign: 'right', color: '#FFFFFF' }}>
                          {activeAdjustment.r_squared != null ? Number(activeAdjustment.r_squared).toFixed(4) : 'N/A'}
                        </Typography>
                        
                        {activeAdjustment.correlation_pearson != null && (
                          <>
                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.75)', fontWeight: 500 }}>Pearson:</Typography>
                            <Typography variant="caption" sx={{ fontWeight: 700, textAlign: 'right', color: '#FFFFFF' }}>
                              {Number(activeAdjustment.correlation_pearson).toFixed(4)}
                            </Typography>
                          </>
                        )}
                        
                        {activeAdjustment.correlation_spearman != null && (
                          <>
                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.75)', fontWeight: 500 }}>Spearman:</Typography>
                            <Typography variant="caption" sx={{ fontWeight: 700, textAlign: 'right', color: '#FFFFFF' }}>
                              {Number(activeAdjustment.correlation_spearman).toFixed(4)}
                            </Typography>
                          </>
                        )}

                        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.75)', fontWeight: 500 }}>Offset:</Typography>
                        <Typography variant="caption" sx={{ fontWeight: 700, textAlign: 'right', color: '#FFFFFF' }}>
                          {activeAdjustment.offset_seconds != null ? `${Number(activeAdjustment.offset_seconds).toFixed(3)}s` : 'N/A'}
                        </Typography>

                        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.75)', fontWeight: 500 }}>Drift:</Typography>
                        <Typography variant="caption" sx={{ fontWeight: 700, textAlign: 'right', color: '#FFFFFF' }}>
                          {activeAdjustment.scale_factor != null ? Number(activeAdjustment.scale_factor).toFixed(6) : 'N/A'}
                        </Typography>
                      </Box>
                      
                      <Button 
                        size="small" 
                        variant="contained" 
                        fullWidth
                        sx={{ 
                          mt: 2, 
                          fontSize: '0.65rem', 
                          height: 24, 
                          bgcolor: 'rgba(255, 255, 255, 0.1)',
                          color: '#FFFFFF',
                          '&:hover': {
                            bgcolor: 'rgba(255, 255, 255, 0.2)',
                          },
                          textTransform: 'none',
                        }}
                        onClick={() => setInfoDialogOpen(true)}
                      >
                        Ver detalles del algoritmo
                      </Button>
                    </Box>
                  }
                >
                  <IconButton
                    size="small"
                    sx={{ 
                      ml: 0.5,
                      color: theme.palette.primary.main,
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.2),
                      }
                    }}
                  >
                    <InfoIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              );
            })()}
          </Box>
          {canReajustarTimestamps && (
            <Button
              variant="outlined"
              size="small"
              onClick={handleReajustarTimestamps}
              disabled={isAdjustingTimestamps}
              startIcon={isAdjustingTimestamps ? <CircularProgress size={16} /> : <SyncIcon />}
              sx={{ mt: 0.5, alignSelf: 'flex-start' }}
            >
              {isAdjustingTimestamps ? 'Ajustando...' : 'Reajustar Timestamps'}
            </Button>
          )}
        </Box>

        {/* Selector de Media Móvil */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              color: theme.palette.text.primary,
              mb: 0.5,
            }}
          >
            Media Móvil
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {[
              { value: null, label: 'Sin media' },
              { value: 5, label: '5 puntos' },
              { value: 10, label: '10 puntos' },
              { value: 20, label: '20 puntos' },
              { value: 50, label: '50 puntos' },
            ].map((option) => {
              const isSelected = movingAverageWindow === option.value;
              return (
                <Box
                  key={option.value ?? 'none'}
                  onClick={() => handleMovingAverageChange(option.value)}
                  sx={{
                    minWidth: 80,
                    height: 32,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0 12px',
                    borderRadius: 1,
                    border: `1px solid ${theme.palette.divider}`,
                    backgroundColor: isSelected
                      ? theme.palette.primary.main
                      : theme.palette.background.paper,
                    color: isSelected
                      ? theme.palette.getContrastText(theme.palette.primary.main)
                      : theme.palette.text.primary,
                    cursor: 'pointer',
                    fontWeight: isSelected ? 600 : 400,
                    fontSize: '0.875rem',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: isSelected
                        ? alpha(theme.palette.primary.main, 0.9)
                        : alpha(theme.palette.primary.main, 0.1),
                      borderColor: theme.palette.primary.main,
                    },
                  }}
                >
                  {option.label}
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* Ajuste fino: solo con timestamps ajustados (el valor se aplica vía API al guardar) */}
        {datos && selectedVariables.length > 0 && !useOriginalTimestamps && activeAdjustment && (
          <Box
            sx={{
              width: '100%',
              flexBasis: '100%',
              display: 'flex',
              gap: 2,
              flexWrap: 'wrap',
              alignItems: 'center',
              p: 1.5,
              borderRadius: 1,
              backgroundColor: alpha(theme.palette.secondary.main, 0.06),
              border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
            }}
          >
            <Tooltip
              arrow
              title={t(
                'ensayos.visualizador.fineOffsetTooltip',
                'Desplaza los timestamps de los sensores respecto al actuador para sincronizar con precisión ambas señales. Mueve el slider y suelta para aplicar, o escribe un valor exacto. Se aplica tanto en Evolución Temporal como en Scatter X-Y.'
              )}
            >
              <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary, minWidth: 180 }}>
                {t('ensayos.visualizador.fineOffsetLabel', 'Ajuste fino offset (sensor)')}: {fineOffsetDisplay.toFixed(1)}s
              </Typography>
            </Tooltip>
            <Box sx={{ flex: 1, minWidth: 250, maxWidth: 500 }}>
              <Slider
                value={fineOffsetDisplay}
                onChange={(_e, val) => setFineOffsetDisplay(val as number)}
                onChangeCommitted={(_e, val) => {
                  const v = clampFineOffsetSeconds(val as number);
                  setFineOffsetDisplay(v);
                  startTransition(() => setFineOffsetSeconds(v));
                  persistFineOffset(v);
                }}
                min={-60}
                max={60}
                step={0.1}
                marks={[
                  { value: -60, label: '-60' },
                  { value: -40, label: '-40' },
                  { value: -20, label: '-20' },
                  { value: 0, label: '0' },
                  { value: 20, label: '+20' },
                  { value: 40, label: '+40' },
                  { value: 60, label: '+60' },
                ]}
                valueLabelDisplay="auto"
                valueLabelFormat={(v) => `${v > 0 ? '+' : ''}${v.toFixed(1)}s`}
                size="small"
              />
            </Box>
            <TextField
              size="small"
              label="Offset (s)"
              type="text"
              inputProps={{
                inputMode: 'decimal',
                'aria-label': 'Offset fino segundos',
              }}
              value={
                Number.isFinite(fineOffsetDisplay)
                  ? String(fineOffsetDisplay).replace('.', ',')
                  : ''
              }
              onChange={(e) => {
                const parsed = parseFineOffsetInput(e.target.value);
                if (parsed !== null) {
                  setFineOffsetDisplay(parsed);
                }
              }}
              onBlur={(e) => {
                const parsed = parseFineOffsetInput(e.target.value);
                if (parsed === null) {
                  setFineOffsetDisplay(fineOffsetSeconds);
                  return;
                }
                const c = clampFineOffsetSeconds(parsed);
                if (c !== parsed) {
                  showWarning(
                    t(
                      'ensayos.visualizador.fineOffsetClamped',
                      'El offset fino debe estar entre {{min}} y {{max}} s (se ha ajustado el valor).',
                      { min: FINE_OFFSET_MIN, max: FINE_OFFSET_MAX }
                    )
                  );
                }
                setFineOffsetDisplay(c);
                startTransition(() => setFineOffsetSeconds(c));
                persistFineOffset(c);
              }}
              onKeyDown={(e) => {
                if (e.key !== 'Enter') return;
                const parsed = parseFineOffsetInput((e.target as HTMLInputElement).value);
                if (parsed === null) return;
                const c = clampFineOffsetSeconds(parsed);
                if (c !== parsed) {
                  showWarning(
                    t(
                      'ensayos.visualizador.fineOffsetClamped',
                      'El offset fino debe estar entre {{min}} y {{max}} s (se ha ajustado el valor).',
                      { min: FINE_OFFSET_MIN, max: FINE_OFFSET_MAX }
                    )
                  );
                }
                setFineOffsetDisplay(c);
                startTransition(() => setFineOffsetSeconds(c));
                persistFineOffset(c);
              }}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 110 }}
            />
            {fineOffsetSeconds !== 0 && (
              <Button
                size="small"
                variant="outlined"
                onClick={() => { setFineOffsetSeconds(0); setFineOffsetDisplay(0); persistFineOffset(0); }}
              >
                {t('ensayos.visualizador.resetFineOffset', 'Resetear')}
              </Button>
            )}
          </Box>
        )}

        {/* Selector de Eje X (solo con timestamps enlazados, en modo temporal) */}
        {hasTimestampsEnlazados && chartType === 'temporal' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 200 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
              {t('ensayos.visualizador.ejeX', 'Eje X')}
            </Typography>
            <FormControl size="small" fullWidth>
              <InputLabel id="eje-x-select-label">{t('ensayos.visualizador.ejeXVariableLabel', 'Variable eje X')}</InputLabel>
              <Select
                labelId="eje-x-select-label"
                value={xAxisVariable?.dataKey ?? '__time__'}
                label={t('ensayos.visualizador.ejeXVariableLabel', 'Variable eje X')}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === '__time__') {
                    setXAxisVariable(null);
                  } else {
                    const opt = xAxisVariableOptions.find((o) => o.dataKey === v);
                    if (opt) setXAxisVariable(opt);
                  }
                }}
              >
                <MenuItem value="__time__">{t('ensayos.visualizador.ejeXTiempo', 'Tiempo (s)')}</MenuItem>
                {xAxisVariableOptions.map((opt) => (
                  <MenuItem key={opt.dataKey} value={opt.dataKey}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}

      </Box>

      {/* Popover de Ajuste de Ejes Y */}
      <Popover
        open={axisSettingsOpen}
        anchorEl={axisSettingsAnchorRef.current}
        onClose={() => setAxisSettingsOpen(false)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Box sx={{ p: 2, minWidth: 320, maxWidth: 400 }}>
          <Typography
            variant="h6"
            sx={{
              mb: 2,
              fontWeight: 600,
              color: theme.palette.text.primary,
            }}
          >
            Ajustar Ejes Y
          </Typography>

          {/* Eje Primario */}
          <Box sx={{ mb: 3 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 1.5,
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  color: primaryColor,
                }}
              >
                Eje Primario
              </Typography>
              {(primaryAxisMin !== undefined || primaryAxisMax !== undefined) && (
                <Button
                  size="small"
                  onClick={handleResetPrimaryAxis}
                  sx={{ minWidth: 'auto', px: 1, fontSize: '0.75rem' }}
                >
                  Restaurar
                </Button>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <TextField
                size="small"
                label="Mínimo"
                type="number"
                value={primaryAxisMin ?? ''}
                onChange={(e) => {
                  const value = e.target.value === '' ? undefined : parseFloat(e.target.value);
                  setPrimaryAxisMin(isNaN(value as number) ? undefined : value);
                }}
                placeholder={
                  autoAxisValues.primary.min !== undefined
                    ? autoAxisValues.primary.min.toFixed(2)
                    : 'Automático'
                }
                InputLabelProps={{ shrink: true }}
                fullWidth
                helperText={
                  autoAxisValues.primary.min !== undefined
                    ? `Auto: ${autoAxisValues.primary.min.toFixed(2)}`
                    : 'Automático'
                }
                error={
                  primaryAxisMin !== undefined &&
                  primaryAxisMax !== undefined &&
                  primaryAxisMin >= primaryAxisMax
                }
              />
              <TextField
                size="small"
                label="Máximo"
                type="number"
                value={primaryAxisMax ?? ''}
                onChange={(e) => {
                  const value = e.target.value === '' ? undefined : parseFloat(e.target.value);
                  setPrimaryAxisMax(isNaN(value as number) ? undefined : value);
                }}
                placeholder={
                  autoAxisValues.primary.max !== undefined
                    ? autoAxisValues.primary.max.toFixed(2)
                    : 'Automático'
                }
                InputLabelProps={{ shrink: true }}
                fullWidth
                helperText={
                  autoAxisValues.primary.max !== undefined
                    ? `Auto: ${autoAxisValues.primary.max.toFixed(2)}`
                    : 'Automático'
                }
                error={
                  primaryAxisMin !== undefined &&
                  primaryAxisMax !== undefined &&
                  primaryAxisMin >= primaryAxisMax
                }
              />
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Eje Secundario */}
          <Box sx={{ mb: 2 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 1.5,
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  color: secondaryAxisColor,
                }}
              >
                Eje Secundario
              </Typography>
              {(secondaryAxisMin !== undefined || secondaryAxisMax !== undefined) && (
                <Button
                  size="small"
                  onClick={handleResetSecondaryAxis}
                  sx={{ minWidth: 'auto', px: 1, fontSize: '0.75rem' }}
                >
                  Restaurar
                </Button>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <TextField
                size="small"
                label="Mínimo"
                type="number"
                value={secondaryAxisMin ?? ''}
                onChange={(e) => {
                  const value = e.target.value === '' ? undefined : parseFloat(e.target.value);
                  setSecondaryAxisMin(isNaN(value as number) ? undefined : value);
                }}
                placeholder={
                  autoAxisValues.secondary.min !== undefined
                    ? autoAxisValues.secondary.min.toFixed(2)
                    : 'Automático'
                }
                InputLabelProps={{ shrink: true }}
                fullWidth
                helperText={
                  autoAxisValues.secondary.min !== undefined
                    ? `Auto: ${autoAxisValues.secondary.min.toFixed(2)}`
                    : 'Automático'
                }
                error={
                  secondaryAxisMin !== undefined &&
                  secondaryAxisMax !== undefined &&
                  secondaryAxisMin >= secondaryAxisMax
                }
              />
              <TextField
                size="small"
                label="Máximo"
                type="number"
                value={secondaryAxisMax ?? ''}
                onChange={(e) => {
                  const value = e.target.value === '' ? undefined : parseFloat(e.target.value);
                  setSecondaryAxisMax(isNaN(value as number) ? undefined : value);
                }}
                placeholder={
                  autoAxisValues.secondary.max !== undefined
                    ? autoAxisValues.secondary.max.toFixed(2)
                    : 'Automático'
                }
                InputLabelProps={{ shrink: true }}
                fullWidth
                helperText={
                  autoAxisValues.secondary.max !== undefined
                    ? `Auto: ${autoAxisValues.secondary.max.toFixed(2)}`
                    : 'Automático'
                }
                error={
                  secondaryAxisMin !== undefined &&
                  secondaryAxisMax !== undefined &&
                  secondaryAxisMin >= secondaryAxisMax
                }
              />
            </Box>
          </Box>

          {hasCustomAxisValues && (
            <>
              <Divider sx={{ my: 2 }} />
              <Button
                fullWidth
                variant="outlined"
                size="small"
                onClick={handleResetAllAxes}
                sx={{ mt: 1 }}
              >
                Restaurar Todos los Ejes
              </Button>
            </>
          )}
        </Box>
      </Popover>

      {/* Menú de opciones de exportación */}
      <Menu
        anchorEl={exportMenuAnchor}
        open={Boolean(exportMenuAnchor)}
        onClose={handleExportClose}
      >
        <MenuItem onClick={() => generateExcel('all')}>
          Exportar todo el ensayo (todos los sensores con datos)
        </MenuItem>
        <MenuItem onClick={() => generateExcel('screen')}>
          Exportar variables del gráfico (ejes Y actuales)
        </MenuItem>
      </Menu>

      {/* Ejes de variables */}
      <Box
        sx={{
          mb: 2,
          display: 'flex',
          gap: 1.5,
          width: '100%',
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <EjeVariables
            eje="primary"
            variables={primaryVariables}
            allVariables={selectedVariables}
            hierarchy={hierarchy}
            onVariableMove={handleVariableMove}
            onVariableRemove={handleVariableRemove}
            onVariableAdd={handleVariableAdd}
            color={primaryColor}
            label="Eje Primario"
            colorMap={colorMap}
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <EjeVariables
            eje="secondary"
            variables={secondaryVariables}
            allVariables={selectedVariables}
            hierarchy={hierarchy}
            onVariableMove={handleVariableMove}
            onVariableRemove={handleVariableRemove}
            onVariableAdd={handleVariableAdd}
            color={secondaryAxisColor}
            label="Eje Secundario"
            colorMap={colorMap}
          />
        </Box>
      </Box>

      {/* Pestañas: Evolución temporal / Scatter X-Y */}
      <Box sx={{ mb: 2 }}>
        <Tabs
          value={chartType}
          onChange={(_, v: 'temporal' | 'scatter') => {
            setChartType(v);
          }}
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 },
          }}
        >
          <Tab
            value="temporal"
            label={t('ensayos.visualizador.tabTemporal', 'Evolución temporal')}
            icon={<ShowChartIcon sx={{ fontSize: 18 }} />}
            iconPosition="start"
          />
          <Tab
            value="scatter"
            label={t('ensayos.visualizador.tabScatter', 'Scatter X-Y')}
            icon={<ScatterPlotIcon sx={{ fontSize: 18 }} />}
            iconPosition="start"
          />
        </Tabs>
        {chartType === 'scatter' && useOriginalTimestamps && primaryVariables.length >= 1 && secondaryVariables.length >= 1 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {t(
              'ensayos.visualizador.scatterOriginalWarning',
              'Está viendo el scatter con timestamps originales. Si las fuentes no comparten el mismo reloj, la relación X–Y puede no ser físicamente representativa; para análisis alineado use timestamps ajustados.'
            )}
          </Alert>
        )}
        {chartType === 'scatter' && (primaryVariables.length < 1 || secondaryVariables.length < 1) && (
          <Alert severity="info" sx={{ mt: 2 }}>
            {t('ensayos.visualizador.scatterRequiresAxes', 'Selecciona al menos una variable en el Eje Primario y una en el Eje Secundario para ver el scatter.')}
          </Alert>
        )}
      </Box>

      {chartType === 'scatter' && scatterXVariable && scatterYVariable && scatterData.length > 0 && (
        <Box
          sx={{
            mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center',
            p: 1.5, borderRadius: 1,
            backgroundColor: alpha(theme.palette.primary.main, 0.06),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
            {t('ensayos.visualizador.scatterFilters', 'Filtro scatter')}:
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
              size="small"
              label={t('ensayos.visualizador.timestampFrom', 'Desde (s)')}
              type="number"
              value={scatterInputFrom}
              onChange={(e) => setScatterInputFrom(e.target.value)}
              placeholder={scatterTimestampRange.min.toFixed(1)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 110 }}
              inputProps={{ step: 0.1 }}
            />
            <TextField
              size="small"
              label={t('ensayos.visualizador.timestampTo', 'Hasta (s)')}
              type="number"
              value={scatterInputTo}
              onChange={(e) => setScatterInputTo(e.target.value)}
              placeholder={scatterTimestampRange.max.toFixed(1)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 110 }}
              inputProps={{ step: 0.1 }}
            />
            <Button
              size="small"
              variant="contained"
              onClick={() => {
                setScatterAppliedFrom(scatterInputFrom === '' ? '' : parseFloat(scatterInputFrom));
                setScatterAppliedTo(scatterInputTo === '' ? '' : parseFloat(scatterInputTo));
              }}
            >
              {t('ensayos.visualizador.scatterApplyFilter', 'Aplicar')}
            </Button>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Switch
              checked={showScatterRegression}
              onChange={(e) => setShowScatterRegression(e.target.checked)}
              size="small"
            />
            <Typography variant="body2">
              {t('ensayos.visualizador.regressionLine', 'Regresión lineal')}
            </Typography>
          </Box>
          {(scatterInputFrom !== '' || scatterInputTo !== '') && (
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setScatterInputFrom('');
                setScatterInputTo('');
                setScatterAppliedFrom('');
                setScatterAppliedTo('');
              }}
            >
              {t('ensayos.visualizador.scatterResetFilter', 'Restablecer filtro')}
            </Button>
          )}
        </Box>
      )}

      {/* Layout principal: Gráfico */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <Paper
            sx={{
              p: 1.5,
              // Cuando hay gráfico, no usar flex: 1 para que el tamaño sea dinámico
              // Cuando no hay gráfico, usar flex: 1 para centrar el contenido
              ...(datos && chartSeries.length > 0 
                ? { 
                    display: 'flex', 
                    flexDirection: 'column',
                    flexShrink: 0,
                  }
                : { 
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                  }
              ),
              backgroundColor: theme.palette.background.paper,
            }}
          >
            {isLoading ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flex: 1,
                }}
              >
                <CircularProgress />
              </Box>
            ) : !datos || chartSeries.length === 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flex: 1,
                  flexDirection: 'column',
                  color: theme.palette.text.secondary,
                }}
              >
                {selectedVariables.length > 0 ? (
                  <>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      No hay datos disponibles para el período seleccionado
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      Selecciona variables para visualizar
                    </Typography>
                    <Typography variant="body2">
                      Arrastra variables desde los selectores de arriba o busca y selecciona variables
                    </Typography>
                  </>
                )}
              </Box>
            ) : chartType === 'scatter' && scatterXVariable && scatterYVariable && scatterData.length > 0 ? (
              <Box sx={{ width: '100%' }}>
                <CanvasScatterPlot
                  data={filteredScatterData}
                  xLabel={scatterXVariable.label}
                  yLabel={scatterYVariable.label}
                  primaryColor={primaryColor}
                  height={600}
                  isLoading={isLoadingScatter}
                  showRegression={showScatterRegression}
                  emptyMessage={
                    filteredScatterData.length === 0 && scatterData.length > 0
                      ? t('ensayos.visualizador.scatterNoDataInRange', 'No hay puntos en el rango de timestamps seleccionado.')
                      : t('ensayos.visualizador.scatterNoData', 'Selecciona las variables X e Y para el scatter.')
                  }
                />
              </Box>
            ) : chartType === 'scatter' && (!scatterXVariable || !scatterYVariable) ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flex: 1,
                  flexDirection: 'column',
                  color: theme.palette.text.secondary,
                  py: 4,
                }}
              >
                <ScatterPlotIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
                <Typography variant="body1" sx={{ mb: 1 }}>
                  {t('ensayos.visualizador.scatterRequiresAxes', 'Selecciona al menos una variable en el Eje Primario y una en el Eje Secundario para ver el scatter.')}
                </Typography>
              </Box>
            ) : chartType === 'scatter' && scatterXVariable && scatterYVariable && scatterData.length === 0 ? (
              <Box sx={{ p: 2 }}>
                <Alert severity="info">
                  {t(
                    'ensayos.visualizador.scatterNoAlignedPoints',
                    'No hay puntos temporales comunes entre las dos variables seleccionadas para construir el scatter. Compruebe que ambas series tienen datos en el mismo rango de tiempo.'
                  )}
                </Alert>
              </Box>
            ) : (
              <Box sx={{ width: '100%' }}>
                <AdvancedLineChart
                  data={datos}
                  series={chartSeries}
                  yAxes={chartYAxes}
                  isLoading={isLoading}
                  height={600}
                  primaryColor={primaryColor}
                  secondaryColor={secondaryAxisColor}
                  xAxisMode="seconds"
                  xAxisLabel={xAxisVariable ? undefined : 'Tiempo (s)'}
                  xAxisVariableKey={xAxisVariable?.dataKey}
                  xAxisVariableLabel={xAxisVariable?.label}
                />
              </Box>
            )}
          </Paper>
          {/* Mensaje de puntos muestreados fuera del gráfico, justo debajo de todo el AdvancedLineChart (incluyendo leyenda y mensaje "Total de puntos") */}
          {actuadorDataTotal > actuadorData.length && actuadorData.length > 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              {t(
                'ensayos.dataSampling',
                `Se muestran ${actuadorData.length.toLocaleString()} puntos muestreados de ${actuadorDataTotal.toLocaleString()} registros totales para optimizar el rendimiento del gráfico. El gráfico muestra una representación precisa de los datos.`
              )}
            </Alert>
          )}
        </Box>
      </DndContext>

      {/* Dialog de información sobre el algoritmo */}
      <Dialog
        open={infoDialogOpen}
        onClose={() => setInfoDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Algoritmo de Ajuste de Timestamps
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            El algoritmo sincroniza temporalmente la señal de un sensor con la del actuador. Primero intenta una
            <strong> correlación cruzada con detección de onset</strong> (inicio de rampa); si no es aplicable, recurre a un
            <strong> método robusto basado en detección de eventos y RANSAC</strong>, especialmente efectivo para señales con
            cambios bruscos (escalones) y robusto ante outliers.
          </Typography>

          <Typography variant="h6" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
            Correlación Cruzada con Detección de Onset (método principal):
          </Typography>
          <Typography variant="body2" paragraph>
            Este método alinea las señales incluso cuando sus ejes temporales son completamente disjuntos
            (p. ej. actuador desde ~12 690 s y sensor desde 0 s). Utiliza la fuerza del actuador como referencia.
          </Typography>
          <Box component="ol" sx={{ pl: 3, mb: 2 }}>
            <li>
              <Typography variant="body2" paragraph>
                <strong>Detección independiente del onset:</strong> En cada señal (actuador y sensor), se calcula la derivada suavizada
                y se localiza el primer instante en que la pendiente supera un umbral significativo de forma sostenida. Ese punto
                corresponde al inicio de la primera rampa de carga.
              </Typography>
            </li>
            <li>
              <Typography variant="body2" paragraph>
                <strong>Cálculo del offset base:</strong> El desfase inicial se calcula como la diferencia entre ambos onsets:
                <code style={{ display: 'block', marginTop: '0.5rem', padding: '0.5rem', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                  offset = t_onset_actuador − t_onset_sensor
                </code>
              </Typography>
            </li>
            <li>
              <Typography variant="body2" paragraph>
                <strong>Refinamiento por correlación cruzada:</strong> Si, tras aplicar el offset base, las señales tienen un tramo
                temporal en común suficiente, se refina el desfase mediante correlación cruzada normalizada (z-score) para ajustar
                con mayor precisión.
              </Typography>
            </li>
          </Box>
          
          <Typography variant="h6" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
            Pipeline del Algoritmo Robusto (si el método principal no es aplicable):
          </Typography>
          <Box component="ol" sx={{ pl: 3, mb: 2 }}>
            <li>
              <Typography variant="body2" paragraph>
                <strong>Resampleo uniforme:</strong> Ambas señales se resamplean a una malla temporal común (0.1s por defecto)
                usando interpolación lineal para tener puntos de datos alineados temporalmente.
              </Typography>
            </li>
            <li>
              <Typography variant="body2" paragraph>
                <strong>Normalización y derivadas:</strong> Se calcula la derivada de cada señal con suavizado ligero,
                y se normaliza mediante z-score para enfatizar los cambios bruscos (escalones) y evitar problemas de escala
                entre señales con amplitudes distintas.
              </Typography>
            </li>
            <li>
              <Typography variant="body2" paragraph>
                <strong>Estimación inicial de lag:</strong> Se calcula la correlación cruzada normalizada de las derivadas
                para obtener una estimación inicial del desfase temporal (τ₀). Esto proporciona un punto de partida cercano
                a la alineación correcta.
              </Typography>
            </li>
            <li>
              <Typography variant="body2" paragraph>
                <strong>Detección de eventos/escalones:</strong> Se localizan los instantes de cambio brusco (picos en la derivada)
                usando un umbral robusto basado en z-score. Se validan que los escalones sean <strong>significativos y sostenidos
                durante al menos 3 segundos</strong>, garantizando que sean cambios reales y no ruido. Para la señal target,
                se priorizan los saltos tempranos (primeros 60 segundos) cuando hay múltiples candidatos.
              </Typography>
            </li>
            <li>
              <Typography variant="body2" paragraph>
                <strong>Emparejamiento de eventos:</strong> Se emparejan los eventos detectados entre ambas señales por proximidad
                temporal (greedy matching) con una tolerancia configurable, tras aplicar el desplazamiento inicial τ₀.
              </Typography>
            </li>
            <li>
              <Typography variant="body2" paragraph>
                <strong>Ajuste afín robusto (RANSAC):</strong> Se ajusta un modelo afín <code>t_B ≈ a + b × t_A</code> usando
                RANSAC (Random Sample Consensus), que es robusto a outliers. Esto proporciona:
                <ul style={{ marginTop: '0.5rem', paddingLeft: '1.5rem' }}>
                  <li><strong>a = offset</strong> (desplazamiento temporal en segundos)</li>
                  <li><strong>b = escala</strong> (factor de corrección de deriva del reloj)</li>
                </ul>
              </Typography>
            </li>
            <li>
              <Typography variant="body2" paragraph>
                <strong>Corrección de drift:</strong> El factor de escala (b) corrige la deriva del reloj cuando 1 segundo en un
                datalogger no es igual a 1 segundo en otro. El modelo final aplicado es:
                <code style={{ display: 'block', marginTop: '0.5rem', padding: '0.5rem', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                  timestamp_ajustado = timestamp_original × scale_factor + offset_seconds
                </code>
              </Typography>
            </li>
            <li>
              <Typography variant="body2" paragraph>
                <strong>Método fallback:</strong> Si no se detectan suficientes eventos (menos de 2), el algoritmo utiliza
                automáticamente un método alternativo basado en correlación cruzada de derivadas estandarizadas con diferentes
                ventanas de suavizado, seleccionando la que produce el mayor R².
              </Typography>
            </li>
            <li>
              <Typography variant="body2" paragraph>
                <strong>Validación y métricas:</strong> Se calculan múltiples métricas de calidad:
                <ul style={{ marginTop: '0.5rem', paddingLeft: '1.5rem' }}>
                  <li><strong>R²:</strong> Coeficiente de determinación (0-1, mayor es mejor)</li>
                  <li><strong>Pearson:</strong> Correlación de valores absolutos</li>
                  <li><strong>Spearman:</strong> Correlación de tendencias/ranks</li>
                  <li><strong>Tendencias:</strong> Correlación de derivadas normalizadas</li>
                </ul>
              </Typography>
            </li>
          </Box>

          <Typography variant="h6" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
            Parámetros Técnicos Guardados:
          </Typography>
          <Typography variant="body2" paragraph>
            Para garantizar la reproducibilidad científica, se guardan todos los parámetros utilizados:
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 2 }}>
            <li><Typography variant="body2">Offset calculado (segundos) - desplazamiento temporal</Typography></li>
            <li><Typography variant="body2">Factor de escala (para corrección de drift del reloj)</Typography></li>
            <li><Typography variant="body2">Coeficiente de determinación (R²)</Typography></li>
            <li><Typography variant="body2">Correlación de Pearson (valores absolutos)</Typography></li>
            <li><Typography variant="body2">Correlación de Spearman (tendencias/ranks)</Typography></li>
            <li><Typography variant="body2">Correlación de tendencias (derivadas normalizadas)</Typography></li>
            <li><Typography variant="body2">Ventana de suavizado óptima (si se usó método fallback)</Typography></li>
            <li><Typography variant="body2">Ventanas de suavizado probadas (método fallback)</Typography></li>
            <li><Typography variant="body2">Ventana de búsqueda (segundos, método fallback)</Typography></li>
            <li><Typography variant="body2">Frecuencia de resampleo (Hz)</Typography></li>
            <li><Typography variant="body2">Número de puntos de datos utilizados</Typography></li>
            <li><Typography variant="body2">Versión del algoritmo utilizado</Typography></li>
            <li><Typography variant="body2">Metadatos adicionales (eventos detectados, inliers RANSAC, etc.)</Typography></li>
          </Box>

          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Importante:</strong> Solo puede haber <strong>un ajuste activo por ensayo</strong> que se aplica
              a <strong>todos los sensores del ensayo</strong>. Cuando se crea un nuevo ajuste, el anterior se marca
              automáticamente como 'superseded' (reemplazado). Esto garantiza consistencia en todos los datos del ensayo.
            </Typography>
          </Alert>
          
          <Alert severity="warning" sx={{ mt: 1 }}>
            <Typography variant="body2">
              <strong>Nota técnica:</strong> El algoritmo prioriza la detección de saltos significativos y sostenidos
              (mínimo 3 segundos de duración) para garantizar que los eventos detectados sean cambios reales y no ruido.
              Para señales con rangos de valores muy pequeños, el umbral de detección se ajusta automáticamente.
            </Typography>
          </Alert>

          <Alert severity="info" sx={{ mt: 1 }}>
            <Typography variant="body2">
              <strong>Nota:</strong> Con el método de correlación cruzada con detección de onset, las métricas Pearson/R² pueden ser
              bajas si las señales tienen escalas muy distintas (p. ej. kN vs µε). Esto no indica un mal ajuste — lo relevante es
              que las rampas coincidan visualmente en el gráfico con «Timestamps: Ajustados».
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoDialogOpen(false)}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={exportDialogOpen}
        onClose={() => {
          if (!isExporting) {
            setExportDialogOpen(false);
            setExportDialogKind(null);
          }
        }}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown={isExporting}
      >
        <DialogTitle>
          {exportDialogKind === 'all'
            ? t('ensayos.visualizador.exportDialogTitleAll', 'Exportación completa')
            : t('ensayos.visualizador.exportDialogTitleScreen', 'Exportación del gráfico')}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 3 }}>
          <CircularProgress />
          <Typography variant="body1" align="center">
            {isExporting
              ? t(
                  'ensayos.visualizador.exportDialogStarting',
                  'Preparando exportación…'
                )
              : exportDialogKind === 'all'
                ? t(
                    'ensayos.visualizador.exportDialogAllBackground',
                    'Generando Excel con todos los sensores (suele tardar 2–6 min). Puede cerrar este diálogo; le avisaremos en notificaciones cuando termine.'
                  )
                : t(
                    'ensayos.visualizador.exportDialogScreen',
                    'Generando Excel con las variables del gráfico (~10–20 s)…'
                  )}
          </Typography>
        </DialogContent>
        <DialogActions>
          {!isExporting && exportDialogKind === 'all' && (
            <Button
              onClick={() => {
                setExportDialogOpen(false);
                setExportDialogKind(null);
              }}
            >
              {t('ensayos.visualizador.exportDialogContinueBackground', 'Seguir en segundo plano')}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

